import { FocusMonitor } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  isDevMode,
  OnDestroy,
  output,
  signal,
  untracked,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxIcon } from '../icon/icon';
import { HxLabel } from '../label/label';

export type SubSectionStatus = 'completed' | 'inprogress' | 'pending';

type StatusPresentation = {
  label: string;
  ariaDescription: string;
  icon: string;
};

const STATUS_PRESENTATION: Record<SubSectionStatus, StatusPresentation> = {
  completed: {
    label: 'Completed',
    ariaDescription: 'Section marked as completed',
    icon: 'ccs-check2',
  },
  inprogress: {
    label: 'In progress',
    ariaDescription: 'Section in progress',
    icon: 'ccs-clock',
  },
  pending: {
    label: 'Pending',
    ariaDescription: 'Section pending review',
    icon: 'ccs-exclamation-lg',
  },
};

const ALLOWED_STATUSES: readonly SubSectionStatus[] = ['completed', 'inprogress', 'pending'];

interface SetExpandedOptions {
  emit?: boolean;
}

@Component({
  selector: 'hx-sub-section',
  standalone: true,
  templateUrl: './sub-section.html',
  styleUrls: ['./sub-section.css', './sub-section-tokens.css'],
  imports: [HxIcon, HxLabel],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'hx-sub-section',
    '[class.hx-sub-section--expanded]': 'isExpanded()',
    '[class.hx-sub-section--animating]': 'isAnimating()',
    '[class.hx-sub-section--focused]': 'isFocused()',
    '[attr.data-status]': 'normalizedStatus()',
  },
})
export class HxSubSection extends HxBaseControl implements AfterViewInit, OnDestroy {
  private static nextId = 0;
  private static activeInstanceSignal = signal<HxSubSection | null>(null);

  private readonly focusMonitor = inject(FocusMonitor);
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly headerButtonRef = viewChild<ElementRef<HTMLButtonElement>>('headerButton');
  private readonly contentRegionRef = viewChild<ElementRef<HTMLDivElement>>('contentRegion');
  private readonly bodyRegionRef = viewChild<ElementRef<HTMLDivElement>>('bodyRegion');

  private readonly instanceId = HxSubSection.nextId++;

  readonly heading = input<string>('');
  readonly headingLevel = input<number>(3);
  readonly icon = input<string | null>(null);
  readonly status = input<SubSectionStatus | string>('pending');
  readonly showStatus = input<boolean>(true);
  readonly expanded = input<boolean>(false);
  readonly disableAnimation = input<boolean>(false);

  /** Subtitle displayed below the heading. */
  readonly subtitle = input<string>('');

  /** Two-way binding output for expanded state */
  readonly expandedChange = output<boolean>();

  protected readonly headerId = `hx-sub-section-header-${this.instanceId}`;
  protected readonly contentId = `hx-sub-section-content-${this.instanceId}`;
  protected readonly statusId = `hx-sub-section-status-${this.instanceId}`;

  protected readonly isExpanded = signal(false);
  protected readonly isAnimating = signal(false);
  protected readonly contentHeight = signal(0);

  protected readonly isFocused = computed(() => HxSubSection.activeInstanceSignal() === this);

  protected readonly normalizedStatus = computed<SubSectionStatus>(() => this.normalizeStatus(this.status()));
  protected readonly statusPresentation = computed(() => STATUS_PRESENTATION[this.normalizedStatus()]);
  protected readonly srStatusText = computed(() => this.statusPresentation().ariaDescription);
  protected readonly headingLevelAttr = computed(() => this.normalizeHeadingLevel(this.headingLevel()));
  protected readonly headerDescribedBy = computed(() =>
    this.showStatus() && this.srStatusText() ? this.statusId : null
  );

  private animationTimer: ReturnType<typeof setTimeout> | null = null;
  private resizeObserver?: ResizeObserver;

  constructor() {
    super();
    effect(() => {
      const parentExpanded = this.expanded();
      // Read isExpanded without tracking it to avoid a feedback loop:
      // setExpandedState() reads isExpanded() internally, which would make
      // the effect re-run on every user toggle and overwrite it with the
      // parent input value.
      if (untracked(this.isExpanded) === parentExpanded) return;
      this.isExpanded.set(parentExpanded);
      this.startAnimation();
    });
  }

  ngAfterViewInit(): void {
    this.monitorFocus();
    this.observeContentHeight();
  }

  ngOnDestroy(): void {
    const headerButton = this.headerButtonRef();
    if (headerButton) {
      this.focusMonitor.stopMonitoring(headerButton);
    }
    if (this.animationTimer) {
      clearTimeout(this.animationTimer);
      this.animationTimer = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    // Clear active instance if this component was the active one
    if (HxSubSection.activeInstanceSignal() === this) {
      HxSubSection.activeInstanceSignal.set(null);
    }
  }

  @HostListener('focusin')
  protected onFocusIn(): void {
    HxSubSection.activeInstanceSignal.set(this);
  }

  /** Toggle via user interaction */
  protected toggle(): void {
    this.setExpandedState(!this.isExpanded());
  }

  /** Imperative open for parent controllers */
  open(): void {
    this.setExpandedState(true);
  }

  /** Imperative close for parent controllers */
  close(): void {
    this.setExpandedState(false);
  }

  /** Sync helper used by parent Expand All flows without emitting events */
  syncExpanded(value: boolean): void {
    this.setExpandedState(value, { emit: false });
  }

  private setExpandedState(value: boolean, options: SetExpandedOptions = {}): void {
    if (this.isExpanded() === value) {
      return;
    }

    this.isExpanded.set(value);
    this.startAnimation();

    if (options.emit !== false) {
      this.expandedChange.emit(value);
    }
  }

  private startAnimation(): void {
    if (this.animationTimer) {
      clearTimeout(this.animationTimer);
      this.animationTimer = null;
    }

    if (this.disableAnimation()) {
      this.isAnimating.set(false);
      return;
    }

    this.isAnimating.set(true);
    const duration = this.getAnimationDuration();

    this.animationTimer = setTimeout(() => {
      this.isAnimating.set(false);
      this.animationTimer = null;
    }, duration);
  }

  private monitorFocus(): void {
    const headerButton = this.headerButtonRef();
    if (headerButton) {
      this.focusMonitor.monitor(headerButton, true);
    }
  }

  private observeContentHeight(): void {
    const bodyRegion = this.bodyRegionRef();
    if (!bodyRegion) {
      return;
    }

    const updateHeight = (): void => {
      const scrollHeight = bodyRegion.nativeElement.scrollHeight;
      this.contentHeight.set(scrollHeight);
    };

    updateHeight();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => updateHeight());
    this.resizeObserver.observe(bodyRegion.nativeElement);
  }

  private getAnimationDuration(): number {
    try {
      const computedStyle = getComputedStyle(this.hostElement.nativeElement).getPropertyValue(
        '--hx-sub-section-transition-duration'
      );
      if (!computedStyle) {
        return 250;
      }
      const value = computedStyle.trim();
      if (value.endsWith('ms')) {
        return Number.parseFloat(value.replace('ms', '').trim());
      }
      if (value.endsWith('s')) {
        return Number.parseFloat(value.replace('s', '').trim()) * 1000;
      }
      const parsed = Number.parseFloat(value);
      return Number.isNaN(parsed) ? 250 : parsed;
    } catch {
      return 250;
    }
  }

  private normalizeStatus(rawValue: string | SubSectionStatus | null | undefined): SubSectionStatus {
    const normalized = (rawValue ?? 'pending').toString().toLowerCase() as SubSectionStatus;
    if (ALLOWED_STATUSES.includes(normalized)) {
      return normalized;
    }

    if (isDevMode()) {
      console.warn(`HxSubSection: Unsupported status "${rawValue}". Falling back to "pending".`);
    }
    return 'pending';
  }

  private normalizeHeadingLevel(value: number | null | undefined): number {
    const numericValue = Number(value);
    const level = Number.isFinite(numericValue) ? Math.floor(numericValue) : 3;
    return Math.min(6, Math.max(1, level));
  }
}
